import { Box, ButtonBase, FormControl, FormHelperText, FormLabel, Stack, Typography, useTheme } from '@mui/material';
import type { V2SingleSelectElement } from '../../../types/V2Types';
import type { V2ElementRendererProps } from './elementProps';
import { refineFonts } from '@/Theming/refineFonts';

/**
 * HRR v2 single-select renderer.
 *
 * Renders each option as a v1-style card button (matching legacy
 * `multipleChoiceQuestion.tsx`): rectangular shell, bold title, optional
 * subtitle, side-by-side on >=sm and stacked on xs. Visual tokens reuse
 * the same `playbookIconButton` theme keys and `refineFonts` typography
 * the legacy questionnaire uses, so HRR v2 looks byte-identical to v1.
 *
 * Selection: the card on click calls `onChange(opt.key)`; format unchanged
 * vs the previous radio implementation. Keyboard accessibility is provided
 * by `ButtonBase` (Tab/Enter/Space all work).
 *
 * data-cy is preserved for E2E continuity:
 *   - `hrr-element-${elementId}` on the group container
 *   - `hrr-option-${elementId}-${opt.key}` on each card
 *   - `hrr-option-${elementId}-${opt.key}-title` on the primary label
 *   - `hrr-option-${elementId}-${opt.key}-subtitle` on the optional description
 *     (RND-4243 — added so the Figma "title + subtitle" two-line hierarchy is
 *     individually selectable in E2E without scraping the card text).
 */
export function SingleSelectElement({
	element,
	value,
	errors,
	disabled,
	onChange,
}: V2ElementRendererProps<V2SingleSelectElement>) {
	const theme = useTheme();
	const stringValue = typeof value === 'string' ? value : '';
	const errorMessage = errors[0]?.message;
	const hasError = errors.length > 0;
	// Stable id so the radiogroup container can describe itself via the error
	// helper text — screen readers announce the violation on the group (RND-4285).
	const errId = `hrr-error-${element.elementId}`;
	// Match v1 `Answer.style.tsx` tokens. `playbookIconButton` carries the
	// canonical background + selected border colors for the cards.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const palette = theme.palette as any;
	const cardBg = palette.playbookIconButton?.backgroundColor ?? theme.palette.action.hover;
	const selectedBg = palette.playbookIconButton?.selectedBackgroundColor ?? theme.palette.common.white;
	const selectedBorder =
		palette.playbookIconButton?.selectedBorderColor ?? theme.palette.primary.main;

	// Capped at 4 cols so a single oversized card never appears alone in a
	// 5-option layout. Stacked on xs, side-by-side from sm upwards.
	const wideCols = Math.min(element.options.length, 4);

	return (
		<FormControl error={hasError} required={element.required} disabled={disabled} component="fieldset" fullWidth>
			<FormLabel component="legend" sx={{ ...refineFonts.smallRegular, color: 'text.secondary', mb: '12px' }}>
				{element.label}
			</FormLabel>
			<Box
				role="radiogroup"
				aria-invalid={hasError}
				aria-describedby={hasError ? errId : undefined}
				data-cy={`hrr-element-${element.elementId}`}
				data-element-type="singleSelect"
				sx={{
					display: 'grid',
					// Stack on truly-mobile widths; from ~480px upwards lay cards
					// side-by-side. The PQ theme's `sm` breakpoint is 750px which
					// is unnecessarily wide for two short Yes/No cards, so the
					// media query is hard-coded against the v1 card layout intent.
					gridTemplateColumns: '1fr',
					'@media (min-width: 480px)': {
						gridTemplateColumns: `repeat(${wideCols}, minmax(0, 1fr))`,
					},
					gap: '16px',
					justifyContent: 'center',
					width: '100%',
				}}>
				{element.options.map((opt) => {
					const selected = stringValue === opt.key;
					return (
						<ButtonBase
							key={opt.key}
							role="radio"
							aria-checked={selected}
							disabled={disabled}
							data-cy={`hrr-option-${element.elementId}-${opt.key}`}
							onClick={() => onChange(opt.key)}
							sx={{
								borderRadius: '8px',
								backgroundColor: selected ? selectedBg : cardBg,
								border: `3px solid ${selected ? selectedBorder : 'transparent'}`,
								minHeight: '120px',
								px: '24px',
								py: '20px',
								textAlign: 'center',
								justifyContent: 'center',
								transition: 'border-color 120ms ease-in-out, background-color 120ms ease-in-out',
								'&:hover': {
									backgroundColor: selected ? selectedBg : theme.palette.action.hover,
								},
								'&.Mui-disabled': {
									opacity: 0.5,
									pointerEvents: 'none',
								},
							}}>
							<Stack gap="6px" alignItems="center" justifyContent="center" width="100%">
								<Typography
									data-cy={`hrr-option-${element.elementId}-${opt.key}-title`}
									sx={{
										...refineFonts.xLargeBold,
										color: theme.palette.primary.dark,
									}}>
									{opt.label}
								</Typography>
								{opt.description ? (
									<Typography
										data-cy={`hrr-option-${element.elementId}-${opt.key}-subtitle`}
										sx={{
											...refineFonts.mediumRegular,
											color: theme.palette.text.secondary,
											whiteSpace: 'pre-line',
										}}>
										{opt.description}
									</Typography>
								) : null}
							</Stack>
						</ButtonBase>
					);
				})}
			</Box>
			{(errorMessage || element.description) && (
				<FormHelperText id={errId} sx={{ mt: '12px' }}>
					{errorMessage ?? element.description}
				</FormHelperText>
			)}
		</FormControl>
	);
}
